import { FatTransactionsPageAllocationTransactionsQuery } from '@/__generated__/graphql';
import client from '@/apollo-client';
import { ApprovedIcon } from '@/assets/icons/info-icons/approved';
import { InfoIcon } from '@/assets/icons/info-icons/info';
import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { Banner } from '@/components/basicComponents/banner';
import FileUpload from '@/components/basicComponents/fileUpload';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import RadioButton from '@/components/basicComponents/radioButton';
import { DropFileArea } from '@/components/dueDiligence-page/dueDiligenceRecord/dropFileArea';
import { ErrorText } from '@/components/basicComponents/input';
import { useErrorMessages } from '@/hooks/i18n';
import { themeVar } from '@/styles/theme';
import axios from 'axios';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { ALLOCATION_QUERY } from '../queries';
import { AllocationWithUpdated } from '../table/table';

interface IForm {
  skipUpload: boolean;
  file: File | null;
}

const defaultFormValues: IForm = {
  skipUpload: false,
  file: null
};

interface MarkAsSignedByManagerProps {
  isOpen: boolean;
  currentRow: AllocationWithUpdated;
  onClose: () => void;
  updateLocalAllocation: (updatedAllocation: FatTransactionsPageAllocationTransactionsQuery['allocationTransactions']['allocations'][number]) => void;
  startFlashRowAnimation: (allocationId: string) => void;
  operationWorkflowTransitionTaskSummariesRefetch: () => void;
}

const MarkAsSignedByManager = ({
  isOpen,
  currentRow,
  onClose,
  updateLocalAllocation,
  startFlashRowAnimation,
  operationWorkflowTransitionTaskSummariesRefetch
}: MarkAsSignedByManagerProps) => {
  const { default: defaultErrorMessage } = useErrorMessages();
  const { control, setValue, handleSubmit, watch } = useForm<IForm>({ defaultValues: defaultFormValues });

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [uploadFilesLoading, setUploadFilesLoading] = useState(false);
  const [drag, setDrag] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);

  const onSave = async (data: IForm) => {
    try {
      if (!currentRow?.id) {
        setErrorMessage(defaultErrorMessage);
        return;
      }

      setUploadFilesLoading(true);
      setErrorMessage(null);
      const { skipUpload, file } = data;

      const formData = new FormData();
      if (file && !skipUpload) {
        formData.append('file', file);
      }
      formData.append('allocationId', currentRow.id);
      formData.append('action', 'markAsSignedByManager');
      formData.append('actionDataPayload', JSON.stringify({ allocationId: currentRow.id, ...(file && { file }), skipUpload: skipUpload.toString() }));

      await axios.post(`${process.env.REACT_APP_BACKEND_BASE_URL}/allocation-operations/execute-action`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      operationWorkflowTransitionTaskSummariesRefetch();
      startFlashRowAnimation(currentRow.id);
      setShowSuccessDialog(true);
    } catch (error) {
      setErrorMessage(defaultErrorMessage);
      console.error(error);
    } finally {
      setUploadFilesLoading(false);
    }
  };

  const onDropHandler = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const uploadFiles = e.dataTransfer.files;
    setDrag(false);
    setValue('file', uploadFiles[0]);
  };

  const handleUploadFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    const uploadFiles = e.target.files;
    setValue('file', uploadFiles[0]);
  };

  const onCloseSuccessDialog = async () => {
    onClose();
    setShowSuccessDialog(false);

    const { data: allocationData } = await client.query({
      query: ALLOCATION_QUERY,
      variables: {
        id: currentRow.id
      }
    });

    updateLocalAllocation(allocationData.Allocation);
  };

  if (showSuccessDialog) {
    return (
      <ModalWrappedContent
        isOpen={showSuccessDialog}
        onClose={onCloseSuccessDialog}
        showCancelButton={false}
        isUseOutsideClick={false}
        confirmButton={{ name: 'OK', onClick: onCloseSuccessDialog }}
      >
        <ModalBody alignItems="center">
          <ApprovedIcon width={60} height={60} />
          <ModalTitle>Manager Signature Completed</ModalTitle>
          <ModalDescription>The document is now fully executed.</ModalDescription>
        </ModalBody>
      </ModalWrappedContent>
    );
  }

  return (
    <ModalWrappedContent
      title="Mark As Signed by Manager"
      isOpen={isOpen}
      onClose={onClose}
      showCloseIcon={!uploadFilesLoading}
      showCancelButton={!uploadFilesLoading}
      isUseOutsideClick={!uploadFilesLoading}
      confirmButton={{ name: 'Confirm', onClick: handleSubmit(onSave), disabled: uploadFilesLoading }}
    >
      {uploadFilesLoading ? (
        <ModalLoader />
      ) : (
        <>
          {errorMessage && (
            <ErrorBannerWrapper>
              <Banner
                icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
                title="Error"
                description={errorMessage}
                bgColor={themeVar('context.error')}
              />
            </ErrorBannerWrapper>
          )}
          <ModalBody>
            <RadioButtonsWrapper>
              <Controller
                name="skipUpload"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <>
                    {['Upload Executed Signature Packet', 'Skip Upload'].map((option, index) => (
                      <RadioButton
                        key={option + index}
                        name="radioButtonValue"
                        radioButtonText={option}
                        selectedOption={value ? 'Skip Upload' : 'Upload Executed Signature Packet'}
                        handleRadioChange={(event) => onChange(event.target.value === 'Skip Upload')}
                      />
                    ))}
                  </>
                )}
              />
            </RadioButtonsWrapper>
            <Banner
              title=""
              description={
                watch('skipUpload')
                  ? 'The fully executed signature packet will not be available through the platform.'
                  : 'Upload the fully executed signature packet to make it available to users on the platform.'
              }
              icon={<InfoIcon width={26} height={26} fill={themeVar('font.base')} />}
              bgColor={themeVar('layer.1')}
              color={themeVar('font.base')}
            />
            {!watch('skipUpload') && (
              <Controller
                name="file"
                control={control}
                rules={{
                  required: {
                    value: true,
                    message: 'File is required'
                  }
                }}
                render={({ field: { value: file }, fieldState: { error } }) => (
                  <>
                    {file ? (
                      <FileUpload
                        type={file.type.includes('image') ? 'image' : 'document'}
                        imageSrc={URL.createObjectURL(file)}
                        fileName={file.name}
                        deleteFile={() => setValue('file', null)}
                      />
                    ) : (
                      <div>
                        <DropFileArea
                          id="signed-signature-packet"
                          handleUploadFile={handleUploadFile}
                          onDropHandler={onDropHandler}
                          drag={drag}
                          setDrag={setDrag}
                        />
                        {error?.message && <ErrorText size="md">{error.message}</ErrorText>}
                      </div>
                    )}
                  </>
                )}
              />
            )}
          </ModalBody>
        </>
      )}
    </ModalWrappedContent>
  );
};

export default MarkAsSignedByManager;

const ModalBody = styled.div<{ alignItems?: string }>`
  display: flex;
  flex-direction: column;
  align-items: ${({ alignItems }) => alignItems || 'stretch'};
  gap: 10px;
`;

const ModalTitle = styled.h2`
  text-align: center;
  font-size: 33px;
  font-weight: 400;
  line-height: 46.2px;
  color: ${themeVar('font.strong')};
`;

const ModalDescription = styled.div`
  font-size: 16px;
  font-weight: 400;
  line-height: 22.4px;
  text-align: center;
  color: ${themeVar('font.base')};
`;

const ErrorBannerWrapper = styled.div`
  margin-bottom: 10px;
`;

const RadioButtonsWrapper = styled.div`
  display: flex;
`;
